import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table } from 'ksnm-common-ui/lib/Table2/Table';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import type { Filter } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { auditStore } from '@stores/auditStore';
import { constantStore } from '@stores/constantStore';
import { dataControlStore } from '@stores/dataControlStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { columns } from './columns';

const { TABLE_NAMES } = constantStore;

export const GeneralReport = observer(() => {
  const { filters } = dataControlStore;

  useEffect(() => {
    if (filters.generalReport?.organization) {
      auditStore.createAuditData({
        moduleName: 'Calculator',
        serviceName: 'Controls',
        operationName: 'View_controls',
        message: `Просмотр записей контролей. Общий отчёт по контролям. Организация ${filters.generalReport?.organization}.`,
      });
    }
  }, [filters.generalReport?.organization]);

  const [title, onChangeTable] = useTableTitleWithCount('Общий отчет по контролям');

  const [key, update] = useUpdateTable({ tableName: TABLE_NAMES.GENERAL_REPORT });

  const hardFilter = useMemo(() => {
    const out: Filter[] = [];

    const { organization, status, year, reportGroup, period, reportPackage } = filters.generalReport || {};

    if (organization) {
      out.push({
        name: 'org_code',
        type: FILTER.EQ,
        values: [organization],
        filterIsInverted: false,
      });
    }

    if (status) {
      out.push({
        name: 'status',
        type: FILTER.EQ,
        values: [status],
        filterIsInverted: false,
      });
    }

    if (year) {
      out.push({
        name: 'year',
        type: FILTER.EQ,
        values: [year],
        filterIsInverted: false,
      });
    }

    if (reportGroup) {
      out.push({
        name: 'rep_group_code',
        type: FILTER.EQ,
        values: [reportGroup],
        filterIsInverted: false,
      });
    }

    if (period) {
      out.push({
        name: 'period_code',
        type: FILTER.EQ,
        values: [period],
        filterIsInverted: false,
      });
    }

    if (reportPackage) {
      out.push({
        name: 'report_package_code_cm',
        type: FILTER.EQ,
        values: [reportPackage],
        filterIsInverted: false,
      });
    }

    return out.length ? [out] : undefined;
  }, [filters.generalReport]);

  const extendTableButtons = useMemo(
    () => (
      <>
        <IconButton key="refresh" iconName="refresh" title="Обновить" onClick={update} />
      </>
    ),
    [update],
  );

  if (!filters.generalReport?.organization) return null;

  return (
    <Table
      key={key}
      columns={columns}
      tableTitle={title}
      tableName={TABLE_NAMES.GENERAL_REPORT}
      resource="v_ctrl_main"
      hardFilter={hardFilter}
      onTableChange={onChangeTable}
      showAggregateRow={false}
      externalFetch={ksnmTableStore.externalFetch}
      externalFetchXLSX={getExcelWithAudit(
        `Экспорт отчёта о контроле. Организация ${filters.generalReport?.organization}.`,
      )}
      cellHeight={30}
      extendTableButtons={extendTableButtons}
    />
  );
});
